/**
 * UI lifecycle binding for continuous background energy learning while signed in.
 * The compute scheduler pauses training for input, foreground work, hidden pages and resource
 * constraints. Rust requests fresh labels every 15 active minutes and bounds checkpoint uploads.
 * @module apps/web/src/lib/energyModelHook
 */
import { useEffect } from 'react';
import { energyTrainingRunner, isCapableDevice, resetEnergyTrainingRunner } from './energyModel';

/** Schedule one idle training check for `userId` while `enabled` (signed in and onboarded). */
export function useEnergyModelTraining(userId: string | null | undefined, enabled: boolean): void {
  useEffect(() => {
    if (!enabled || !userId) {
      resetEnergyTrainingRunner();
      return;
    }
    if (!isCapableDevice()) return;
    const runner = energyTrainingRunner(userId);
    // Background training is best-effort; failures leave the planner on the check-in profile.
    runner.run().catch(() => undefined);
    const invalidate = (event: Event) => {
      const detail = (event as CustomEvent<{ ownerId?: string }>).detail;
      if (detail?.ownerId === userId) void runner.invalidateEvidence();
    };
    window.addEventListener('fluxability-energy-evidence-changed', invalidate);
    return () => {
      window.removeEventListener('fluxability-energy-evidence-changed', invalidate);
      runner.cancel();
    };
  }, [userId, enabled]);
}
